import CardClass from "../../CardClass";
import Progress from "../../Progress";
import EmptyState from "../../EmptyState";
import Menu from "../../Menu";
import Exporter from "../../../Utility/Exporter";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIFileDownloadIcon from "@mui/icons-material/FileDownload";
import MUITable from "@mui/material/Table";
import MUITableBody from "@mui/material/TableBody";
import MUITableCell from "@mui/material/TableCell";
import MUITableContainer from "@mui/material/TableContainer";
import MUITableHead from "@mui/material/TableHead";
import MUITableRow from "@mui/material/TableRow";
import MUIPaper from "@mui/material/Paper";
import MUIBox from "@mui/material/Box";
import MUICheckbox from "@mui/material/Checkbox";
import { alpha as MUIAlpha } from "@mui/material/styles";
import Setting from "../../../Utility/Setting";
import MUITooltip from "@mui/material/Tooltip";

export default class CardSalesScorecardResults extends CardClass {
  state = {
    highlightByTier: Setting.get("salesScorecard.highlightByTier") ?? true,
    hoveredColumn: null,
  };

  formatValue = (value, column) => {
    if (value === null || value === undefined) {
      return ""
    };
    if (["tier1Points", "tier2Points", "tier3Points"].includes(column?.name)) {
      return Number.isFinite(value) ? Math.round(value) : value;
    }
    if (column?.type === "number") {
      if (value === 0) {
        return "0"
      };
      const valueAsANumber = Number(value);
      if (Number.isNaN(valueAsANumber)) {
        return value
      };
      return valueAsANumber.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return value;
  };

  getDefaultRowSx = (row) => {
    switch (row.rowType) {
      case "region-total":
      case "totals":
      case "month":
      case "ytd":
        return {
          fontWeight: row.rowType === "region-total" ? 600 : 700,
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.primary.dark
              : theme.palette.primary.light + "33",
        };
      default:
        return {};
    }
  };

  handleHighlightChange() {
    const newHighlightByTierValue =
      this.state.highlightByTier === undefined
        ? true
        : !this.state.highlightByTier;

    this.setState({ highlightByTier: newHighlightByTierValue });
    Setting.set("salesScorecard.highlightByTier", newHighlightByTierValue);
    return newHighlightByTierValue;
  }


  getDefaultCellSx = (row, column, rawValue) => {
    const isSummaryColumn = [
      // "approved_total",
      "ytd_total",
      "ytd_goal",
      "ytd_gap",
      "avg_needed",
    ].includes(column?.name);

    const isTotalsRow = [
      "region-total",
      "totals",
      "ytd"
    ].includes(row?.rowType);


    const columnWidth = this.props.defaultColumnWidths?.find(
      (defaultColumnWidth) => defaultColumnWidth.columnName === column?.name
    )?.width;

    const isTierColumn = ["tier1Points", "tier2Points", "tier3Points"].includes(column?.name);
    const isYtdLightColumn = ["ytd_goal", "ytd_gap"].includes(column?.name);
    const isYtdTotalColumn = column?.name === "ytd_total";
    const isMonthOrYearRow = row.rowType === "month" || row.rowType === "ytd";
    const isNegative = typeof rawValue === "number" && rawValue < 0;

    const isMonthColumn = column?.subType === "monthValue";
    const highlightOn = !!this.state?.highlightByTier;
    const cellNumber = typeof rawValue === "number" ? rawValue : NaN;

    let belowTier1 = false;
    let aboveTier3 = false;
    if (highlightOn && isMonthColumn && row?.monthlyTiers) {
      const tierForMonth = row.monthlyTiers[column.name];

      if (tierForMonth && Number.isFinite(cellNumber)) {
        belowTier1 = Number.isFinite(tierForMonth.tier1Points)
          && cellNumber < tierForMonth.tier1Points;

        aboveTier3 = Number.isFinite(tierForMonth.tier3Points)
          && cellNumber > tierForMonth.tier3Points;
      }
    }
    const isCurrentMonthColumn = !!column?.isCurrentMonth;

    const shouldHighlightMonth = highlightOn && isMonthColumn && !isCurrentMonthColumn && !isTotalsRow && (belowTier1 || aboveTier3);


    return {
      // Force right-align for month and ytd rows
      textAlign: isMonthOrYearRow
        ? "right"
        : column?.align || (column?.type === "number" ? "right" : "left"),
      whiteSpace: "nowrap",
      fontWeight:
        row.rowType && row.rowType !== "location"
          ? 700
          : isSummaryColumn
            ? 600
            : "inherit",
      ...(columnWidth ? { width: columnWidth } : {}),
      // Light background for Tier & YTD Goal/Gap
      ...(isTierColumn || isYtdLightColumn
        ? {
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? MUIAlpha(theme.palette.common.white, 0.12)
              : MUIAlpha(theme.palette.common.black, 0.04),
        }
        : {}),
      ...(column.name !== "regionLocation" && {
        paddingRight: "8px",
        paddingLeft: "0px",
      }),
      // Slightly darker background for YTD Total
      ...(isYtdTotalColumn
        ? {
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? MUIAlpha(theme.palette.common.white, 0.20)
              : MUIAlpha(theme.palette.common.black, 0.06),
        }
        : {}),
      ...(isNegative && column.name != "avg_needed"
        ? { color: (theme) => theme.palette.error.main }
        : {}),
      ...(shouldHighlightMonth
        ? {
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? MUIAlpha(
                belowTier1 ? theme.palette.error.main : theme.palette.success.main,
                0.30
              )
              : MUIAlpha(
                belowTier1 ? theme.palette.error.main : theme.palette.success.main,
                0.15
              ),
        }
        : {}),

    };
  };


  getActions() {
    return [
      <Menu
        key="export"
        menuItems={[
          <MUIMenuItem
            key="csv"
            disabled={this.props.isLoading || !this.props.rows?.length}
            onClick={() => {
              const headers = this.props.columns.map((c) => c.title);
              const keys = this.props.columns.map((c) => c.name);
              const csvRows = [
                headers,
                ...this.props.rows.map((row) =>
                  keys.map((key) => {
                    if (key === "regionLocation") {
                      return row.rowType === "region-total" ? row.region : row.location;
                    }
                    const value = row[key];
                    return value == null ? "" : value;
                  })
                ),
              ];
              Exporter.exportCSV(csvRows, this.props.exportFilename);
            }}
          >
            <MUIListItemIcon>
              <MUIFileDownloadIcon fontSize="small" />
            </MUIListItemIcon>
            Export CSV
          </MUIMenuItem>,
          <MUIMenuItem
            key="toggleHighlight"
            onClick={() => { this.handleHighlightChange() }}
          >
            <MUIListItemIcon>
              <MUICheckbox
                checked={!!this.state.highlightByTier}
                sx={{ paddingTop: 0, paddingBottom: 0 }}
                edge="start"
                size="small"
                tabIndex={-1}
                disableRipple
                inputProps={{ 'aria-labelledby': 'toggle-highlight-tier' }}
              />
            </MUIListItemIcon>
            <span id="toggle-highlight-tier">Highlight by Tier</span>
          </MUIMenuItem>
        ]}
      />,
    ];
  }

  renderContent() {
    if (this.props.isLoading) return <Progress message="Loading Data..." />;

    if (!this.props.rows?.length) {
      return (
        <EmptyState
          line1={this.props.loadedAt ? "No results found" : "Waiting for results"}
        />
      );
    }

    const { columns = [], rows = [] } = this.props;

    return (
      <MUIBox
        sx={{
          paddingBottom: "none",
          height: "calc(100vh - 120px)",
          overflow: "none",
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <MUITableContainer
          component={MUIPaper}
          elevation={0}
          sx={{
            height: "100%",
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <MUITable stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
            <MUITableHead>
              <MUITableRow>
                {columns.map((column) => {
                  const columnWidth = this.props.defaultColumnWidths?.find(
                    (defaultColumnWidth) => defaultColumnWidth.columnName === column.name
                  )?.width;

                  return (
                    <MUITableCell
                      key={column.name}
                      align={column.name === "regionLocation" ? "left" : "right"}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        ...(columnWidth ? { width: columnWidth } : {}),
                      }}
                    >
                      {column.title}
                    </MUITableCell>
                  );
                })}
              </MUITableRow>
            </MUITableHead>

            <MUITableBody>
              {rows.map((row, rowIndex) => {
                const baseRowSx = this.getDefaultRowSx(row);

                return (
                  <MUITableRow key={row.id ?? rowIndex} sx={{
                    ...baseRowSx,
                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.palette.action.hover,
                    },
                  }}>
                    {columns.map((column) => {
                      const raw = row[column.name];
                      const display = column.name === "regionLocation"
                        ? (row.rowType === "region-total" ? row.region : row.location)
                        : this.formatValue(raw, column); const baseCellSx = this.getDefaultCellSx(row, column, raw);

                      const isTierColumn = ["tier1Points", "tier2Points", "tier3Points"].includes(column.name);

                      return (

                        <MUITableCell key={column.name} sx={{ ...baseCellSx }}>
                          {isTierColumn && row.tierTooltip ? (
                            <MUITooltip
                              title={<span style={{ whiteSpace: "pre-line" }}>{row.tierTooltip}</span>}
                              arrow
                              placement="top"
                            >
                              <span>{display}</span>
                            </MUITooltip>
                          ) : (
                            display
                          )}
                        </MUITableCell>
                      );
                    })}
                  </MUITableRow>
                );
              })}
            </MUITableBody>
          </MUITable>
        </MUITableContainer>
      </MUIBox>
    );
  }

  getTitle() {
    return "Results";
  }
}
